import React from 'react';
import { useTranslation } from 'react-i18next';
import { GlobeAltIcon } from '@heroicons/react/outline';
import { UI_LANGUAGES } from '../../i18n/languages';

/**
 * Language picker for the application flow.
 *
 * A native <select> on purpose: it is keyboard accessible and screen-reader
 * labelled without any custom ARIA, and on phones it opens the OS picker, which
 * is the control every applicant already knows how to use. A hand-rolled
 * dropdown would be more on-brand and worse in every way that matters here.
 *
 * Changing language never unmounts the form, so answers already typed survive
 * the switch.
 */
const LanguageSwitcher = ({ className = '' }) => {
  const { t, i18n } = useTranslation();

  const change = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  // i18n.language can carry a region ('es-MX') even though we only ship base
  // languages; resolvedLanguage is what actually got loaded.
  const current = i18n.resolvedLanguage || i18n.language;

  return (
    <div className={`inline-flex items-center ${className}`}>
      <GlobeAltIcon className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" aria-hidden="true" />
      <label htmlFor="language-switcher" className="sr-only">
        {t('languageSwitcher.ariaLabel')}
      </label>
      <select
        id="language-switcher"
        value={current}
        onChange={change}
        className="form-input py-1 pr-8 text-sm w-auto"
      >
        {UI_LANGUAGES.map((language) => (
          // lang= lets the browser and screen readers pronounce each option in
          // its own language rather than reading "Español" as English.
          <option key={language.code} value={language.code} lang={language.code}>
            {language.nativeName}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSwitcher;
